import React, { useEffect, useState } from "react";
import { Typography, Row, Col, Card, Tag, Rate, Button, Input, Select, Checkbox } from "antd";
import { EnvironmentOutlined, ClockCircleOutlined } from "@ant-design/icons";
import styled from "styled-components";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import axios from "axios";
import L from "leaflet";
import { API_BASE_URL } from "../utils/BaseUrl";
import { useNavigate } from 'react-router-dom';


const { Title, Text } = Typography;
const { Option } = Select;

// Header Gradient
const PageHeader = styled.div`
  background: linear-gradient(
    to right,
    rgba(241, 86, 55, 0.85),
    rgba(251, 168, 64, 0.85)
  );
  padding: 40px 0;
  color: white;
  text-align: center;
  margin-bottom: 40px;
`;

// Filter Wrapper – giữ nguyên UI cũ
const FilterCard = styled(Card)`
  margin-bottom: 24px;
`;

// Card hiển thị Gym
const FitnessCard = styled(Card)`
  margin-bottom: 24px;
  transition: transform 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
  }

  .ant-card-cover img {
    height: 200px;
    object-fit: cover;
  }
`;

// Tag giá
const PriceTag = styled(Tag)`
  font-weight: bold;
  font-size: 14px;
  padding: 2px 8px;
`;


delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.3/images/marker-icon-2x.png",
  iconUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.3/images/marker-icon.png",
  shadowUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.3/images/marker-shadow.png",
});


function ChangeView({ center, zoom }) {
  const map = useMap();
  useEffect(() => {
    map.setView(center, zoom);
  }, [center, zoom, map]);
  return null;
}
const PAGE_SIZE = 6; // Số lượng gym mỗi lần load

const ListFitnessCenterPage = () => {
 
  const [userPosition, setUserPosition] = useState([15.58445, 108.15515]); // Vị trí mặc định (Quảng Nam)
  const [gyms, setGyms] = useState([]); // Dùng cho map (kết quả searchNearby)
  const [fitnessCenters, setFitnessCenters] = useState([]); // Dùng cho danh sách thẻ (GET /gyms)
  const [filteredCenters, setFilteredCenters] = useState([]);
  const [imageLoadStatus, setImageLoadStatus] = useState({}); // Lưu trạng thái ảnh đã tải hay chưa

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const navigate = useNavigate();


  const [filters, setFilters] = useState({ // Dùng để lưu các bộ lọc
    priceRange: "",
    facilities: [],
    rating: 0,
  });

  const [facilitiesList, setFacilitiesList] = useState([]); // State lưu danh sách facilities

  const preloadImages = (gyms) => {
      const newImageStatus = {};
      gyms.forEach(gym => {
        if (gym.mainImage) {
          const img = new Image();
          img.onload = () => {
            setImageLoadStatus(prev => ({ ...prev, [gym.id]: true }));
          };
          img.onerror = () => {
            setImageLoadStatus(prev => ({ ...prev, [gym.id]: true }));
          };
          img.src = gym.mainImage;
          newImageStatus[gym.id] = false;
        }
      });
      setImageLoadStatus(prev => ({ ...prev, ...newImageStatus }));
    };

  const fetchAllGyms = async () => {
  setLoading(true);
  try {
    const { data } = await axios.get(`${API_BASE_URL}/api/Gym/gyms?isActive=true`);

    console.log("Ảnh trả về từ BE:", data.map(g => ({ id: g.gymId, image: g.mainImage })));

    const mapped = data.map((g) => {
      let imageUrl = "";

      if (typeof g.mainImage === "string" && g.mainImage.startsWith("http")) {
        imageUrl = g.mainImage;
      } else if (typeof g.mainImage === "string" && g.mainImage.trim() !== "") {
        imageUrl = `${API_BASE_URL}/${g.mainImage.replace(/^\/+/, "")}`;
      } else {
        imageUrl = "/images/placeholder.jpg";
      }

      return {
        id: g.gymId,
        name: g.name,
        location: g.address,
        rating: g.rating,
        price: g.priceRange || "—",
        mainImage: imageUrl,
        tags: [...new Set((g.workoutTypes || []).map((w) => w.name?.toLowerCase?.()))],
        hours: g.openingHours,
        latitude: g.latitude,
        longitude: g.longitude,
        isImageLoaded: false,
      };
    });

    setFitnessCenters(mapped);
    setFilteredCenters(mapped); // Khởi tạo filteredCenters với dữ liệu ban đầu
    // Preload images ngay khi có data
    preloadImages(mapped);
  } catch (err) {
    console.error("Lỗi lấy danh sách gym:", err.response?.data || err.message || err);
    setError("Không thể tải danh sách phòng gym.");
  } finally {
    setLoading(false);
  }
};


  const gymsToShow = filteredCenters.slice(0, currentPage * PAGE_SIZE);

  /*********************************************************
   *  👉  HÀM GỌI API TÌM GYM GẦN VỊ TRÍ (POST /searchNearby)*
   *    - Sử dụng cho phần MAP                               *
   *********************************************************/
  const fetchGymsNearby = async (lat, lng) => {
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API_BASE_URL}/api/Gym/searchNearby`, {
        latitude: lat,
        longitude: lng,
        radiusKm: 5,
      });
      setGyms(data);
    } catch (err) {
      console.error("Lỗi lấy dữ liệu Gym gần vị trí:", err);
      setError("Không thể tải dữ liệu Gym gần vị trí.");
    }
    setLoading(false);
  };

  /*****************************************
   *  👉  LẤY POSITION NGƯỜI DÙNG & GỌI API *
   *****************************************/
  useEffect(() => {
    // Lần đầu load – lấy danh sách toàn bộ gym để hiển thị card
    fetchAllGyms();

    // Đồng thời lấy vị trí hiện tại để hiển thị map
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const { latitude, longitude } = pos.coords;
          setUserPosition([latitude, longitude]);
          fetchGymsNearby(latitude, longitude);
        },
        () => fetchGymsNearby(userPosition[0], userPosition[1])
      );
    } else {
      fetchGymsNearby(userPosition[0], userPosition[1]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /*******************************************
   *  👉  GEOCODING ĐỊA CHỈ => TỌA ĐỘ LAT/LNG *
   *******************************************/
  const geocodeAddress = async (address) => {
    const { data } = await axios.get("https://nominatim.openstreetmap.org/search", {
      params: {
        q: address,
        format: "json",
        limit: 1,
      },
    });
    if (data.length === 0) throw new Error("Không tìm thấy địa chỉ.");
    return [parseFloat(data[0].lat), parseFloat(data[0].lon)];
  };

  /*************************************
   *  👉  HANDLE SEARCH Ô INPUT ĐỊA CHỈ *
   *************************************/
  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchInput) return;

    try {
      const [lat, lng] = await geocodeAddress(searchInput);
      setUserPosition([lat, lng]);
      fetchGymsNearby(lat, lng);
    } catch (err) {
      setError("Không thể tìm thấy vị trí này.");
    }
  };

  const applyFilters = () => {
    let filteredCenters = [...fitnessCenters];
    if (filters.priceRange) {
      filteredCenters = filteredCenters.filter((center) => {
        const price = parseInt(center.price.replace(/[^0-9]/g, "")) || 0;
        if (filters.priceRange === "under500k") return price < 500000;
        if (filters.priceRange === "500k-1m") return price >= 500000 && price <= 1000000;
        if (filters.priceRange === "1m-2m") return price > 1000000 && price <= 2000000;
        if (filters.priceRange === "above2m") return price > 2000000;
        return true;
      });
    }
    if (filters.facilities.length > 0) {
      filteredCenters = filteredCenters.filter((center) =>
        filters.facilities.every((facility) => center.tags.includes(facility))
      );
    }
    if (filters.rating > 0) {
      filteredCenters = filteredCenters.filter((center) => center.rating >= filters.rating);
    }
    return filteredCenters;
  };

  const handlePriceChange = (value) => {
    setFilters((prev) => ({ ...prev, priceRange: value }));
  };

  const handleFacilityChange = (checkedValues) => {
    setFilters((prev) => ({ ...prev, facilities: checkedValues }));
  };

  const handleRatingChange = (value) => {
    setFilters((prev) => ({ ...prev, rating: value }));
  };

  const handleApplyFilters = () => {
    setFilteredCenters(applyFilters());
  };

  useEffect(() => {
    fetchAllGyms();
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const { latitude, longitude } = pos.coords;
          setUserPosition([latitude, longitude]);
          fetchGymsNearby(latitude, longitude);
        },
        () => fetchGymsNearby(userPosition[0], userPosition[1])
      );
    } else {
      fetchGymsNearby(userPosition[0], userPosition[1]);
    }
  }, []);

  useEffect(() => {
    const uniqueFacilities = fitnessCenters.length
      ? [...new Set(fitnessCenters.flatMap((center) => center.tags))]
      : ["Aerobic", "Boxing", "Cycling", "Fitness", "Functional", "Group X", "Tiktok Dance", "Yoga", "Weightlifting", "Indoor Cycling"];
    setFacilitiesList(uniqueFacilities);
  }, [fitnessCenters]);

  // useEffect(() => {
  //   setFilteredCenters(fitnessCenters);
  // }, [fitnessCenters]);

  // useEffect(() => {
  //   setFilteredCenters(fitnessCenters.map((center) => ({ ...center, isImageLoaded: false }))); // THÊM: Khởi tạo isImageLoaded
  // }, [fitnessCenters]);
  /***********************
   *  👉  JSX RENDER      *
   ***********************/
  return (
    <>
      {/* ----------- HEADER & SEARCH BAR ----------- */}
      <PageHeader>
        <div className="container">
          <Title level={2} style={{ color: "white" }}>
            Tìm Trung Tâm Thể Hình Lý Tưởng Của Bạn
          </Title>
        </div>

        <div style={{ padding: "20px" }}>
          {/* FORM SEARCH ĐỊA CHỈ */}
          <form onSubmit={handleSearch} style={{ marginBottom: "45px", display: "flex", alignItems: "center", flexWrap: "wrap", justifyContent: "center" }}>
            <input
              type="text"
              placeholder="Nhập địa chỉ (ví dụ: Thanh Khê, Ngũ Hành Sơn)"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              style={{
                padding: "12px 16px",
                width: "300px",
                maxWidth: "100%",
                marginRight: "10px",
                borderRadius: "20px",
                border: "1px solid #ccc",
                boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
                outline: "none",
                fontSize: "16px",
                transition: "border 0.3s ease",
              }}
              onFocus={(e) => (e.target.style.border = '1px solid #f15637')}
              onBlur={(e) => (e.target.style.border = '1px solid #ccc')}
            />
            <button
              type="submit"
              style={{
                padding: "12px 24px",
                borderRadius: "20px",
                background: "radial-gradient(circle at 18.7% 37.8%, rgb(250, 250, 250) 0%, rgb(225, 234, 238) 90%)",
                color: "#424040",
                border: "none",
                cursor: "pointer",
                fontWeight: "bold",
                fontSize: "16px",
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                transition: "background 0.3s ease",
              }}
              onMouseOver={(e) => (e.target.style.opacity = 0.9)}
              onMouseOut={(e) => (e.target.style.opacity = 1)}
            >
              Tìm kiếm
            </button>
          </form>

          {/* THÔNG BÁO */}
          {error && <div style={{ color: "red", marginBottom: "40px" }}>{error}</div>}
          {loading && <div>Đang tải dữ liệu...</div>}

          {/* ----------- BẢN ĐỒ ----------- */}
          <MapContainer
            center={userPosition}
            zoom={13}
            style={{ height: "400px", width: "100%" }}
            scrollWheelZoom
          >
            <ChangeView center={userPosition} zoom={13} />

            <TileLayer
              attribution='&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />

            {/* Marker người dùng */}
            <Marker position={userPosition}>
              <Popup>Bạn đang ở đây</Popup>
               
            </Marker>

            {/* Marker gym gần vị trí */}
            {gyms.map((gym) => (
            <Marker key={gym.gymId} position={[gym.latitude, gym.longitude]}>
              <Popup>
                <b>{gym.name}</b>
                <br />
                {gym.address}
                <br />
                Khoảng cách: {gym.distanceKm?.toFixed(2)} km
                <br />
                <Button
                type="primary"
                size="small"
                onClick={() => navigate(`/detail-fitness/${gym.gymId}`)}
                style={{ marginTop: 8, fontSize: 12, padding: '0 8px', height: '24px' }}
              >
                Xem chi tiết
              </Button>

              </Popup>
            </Marker>
          ))}

          </MapContainer>
        </div>
      </PageHeader>

      {/* ----------- LIST CARD + FILTER ----------- */}
      <div className="container">
        <Row gutter={24}>
          {/* FILTERS COLUMN – giữ nguyên UI, chưa nối backend filter */}
          <Col xs={24} lg={6}>
            <FilterCard title="Bộ lọc">
              {/* Khoảng giá */}
              <div style={{ marginBottom: 16 }}>
                <Text strong>Khoảng giá</Text>
                <Select
                  placeholder="Chọn khoảng giá"
                  style={{ width: "100%", marginTop: 8 }}
                  onChange={handlePriceChange}
                  value={filters.priceRange}
                >
                  <Option value="">Tất cả</Option>
                  <Option value="under500k">Dưới 500k</Option>
                  <Option value="500k-1m">500k - 1 triệu</Option>
                  <Option value="1m-2m">1 triệu - 2 triệu</Option>
                  <Option value="above2m">Trên 2 triệu</Option>
                </Select>
              </div>

              {/* Tiện ích */}
              <div style={{ marginBottom: 16 }}>
                <Text strong>Tiện ích</Text>
                <div style={{ marginTop: 8 }}>
                  <Checkbox.Group
                    options={facilitiesList}
                    onChange={handleFacilityChange}
                    value={filters.facilities}
                  />
                </div>
              </div>

              {/* Đánh giá */}
              <div style={{ marginBottom: 16 }}>
                <Text strong>Đánh giá</Text>
                <div style={{ marginTop: 8 }}>
                  <Rate
                    value={filters.rating}
                    onChange={handleRatingChange}
                  />
                  <Text style={{ marginLeft: 8 }}>
                    {filters.rating ? `${filters.rating} sao trở lên` : "Bất kỳ"}
                  </Text>
                </div>
              </div>

              <Button type="primary" block onClick={handleApplyFilters}>
                Áp dụng bộ lọc
              </Button>
            </FilterCard>
          </Col>


          {/* RESULTS COLUMN (Cards) */}
          <Col xs={24} lg={18}>
            <div
              style={{
                marginBottom: 24,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Text strong>{filteredCenters.length} results found</Text>
              <Select defaultValue="recommended" style={{ width: 200 }}>
                <Option value="recommended">Recommended</Option>
                <Option value="price-asc">Price: Low to High</Option>
                <Option value="price-desc">Price: High to Low</Option>
                <Option value="rating">Highest Rating</Option>
              </Select>
            </div>

            {/* CARD LIST */}
            {gymsToShow.map((center) => (
              <FitnessCard
                key={center.id}
                hoverable
                cover={
                  <div style={{ position: "relative", height: 200, background: "#f0f0f0" }}>
                    {/* Hiển thị khi ảnh chưa load */}
                    {!imageLoadStatus[center.id] && (
                      <div
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "#888",
                          fontSize: 16,
                          background: "#f2f2f2"
                        }}
                      >
                        Loading Image...
                      </div>
                    )}
                    {/* Ảnh chính */}
                    <img
                      alt={center.name}
                      src={center.mainImage}
                      style={{
                        width: "100%",
                        height: "200px",
                        objectFit: "cover",
                        display: imageLoadStatus[center.id] ? "block" : "none",
                      }}
                      loading="lazy"
                      onLoad={() =>
                        setImageLoadStatus((prev) => ({ ...prev, [center.id]: true }))
                      }
                      onError={(e) => {
                        console.log(`Lỗi tải ảnh cho gym ${center.id}:`, center.mainImage);
                        e.target.src = "/images/placeholder.jpg";
                        setImageLoadStatus((prev) => ({ ...prev, [center.id]: true }));
                      }}
                    />
                  </div>
                }
                actions={[
                  <Button
                    type="primary"
                    block
                    onClick={() => navigate(`/detail-fitness/${center.id}`)}
                  >
                    <p style={{ marginTop: 12, color: 'white' }}>Xem chi tiết</p>
                  </Button>
                ]}
              >
                <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <Title level={4} style={{ marginBottom: 4 }}>{center.name}</Title>
                    <Text type="secondary"><EnvironmentOutlined /> {center.location}</Text>
                    <div style={{ marginTop: 4 }}>
                      <Text type="secondary"><ClockCircleOutlined /> {center.hours}</Text>
                    </div>
                  </div>
                  <PriceTag color="#F15637">{center.price}</PriceTag>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
                  <Rate disabled defaultValue={center.rating} style={{ fontSize: 14 }} />
                  <Text style={{ marginLeft: 8 }}>{center.rating}/5</Text>
                </div>

                <div>
                  {center.tags.map((tag, index) => (
                    <Tag key={`${tag}-${index}`} color="blue" style={{ marginBottom: 4 }}>
                      {tag.charAt(0).toUpperCase() + tag.slice(1)}
                    </Tag>
                  ))}
                </div>
              </FitnessCard>
            ))}


            {/* Nút Load More */}
            {gymsToShow.length < filteredCenters.length && (
              <div style={{ textAlign: "center", margin: "32px 0" }}>
                <Button
                type="primary"
                size="large"
                style={{
                  background: "linear-gradient(107deg, rgb(252, 98, 47) 11.1%, rgb(245, 135, 0) 95.3%)",
                  border: "none",
                  borderRadius: 24,
                  fontWeight: 600,
                  padding: "10px 40px",
                }}
                onClick={() => setCurrentPage((prev) => prev + 1)}
              >
                Xem thêm
              </Button>

              </div>
            )}
            {/* Nếu hết thì không hiện nút */}
          </Col>
        </Row>
      </div>
    </>
  );
};

export default ListFitnessCenterPage;
